module Widgets.Dropdown where

import Common

import Concur.React.DOM as D
import Concur.React.Props (ReactProps)
import Concur.React.Props as P
import Control.MultiAlternative (class MultiAlternative)
import Control.ShiftMap (class ShiftMap)
import Internal.ConcurUtil (loopInfinitely)
import React (ReactClass, ReactElement, unsafeCreateElement)
import React.DOM.Props as RDP


-- | Defines an item in a dropdown menu which will return a result of type `a`
-- | when selected.
type DropdownItem a =
    { label :: String
    , value :: a
    }

renderDropdownItem :: forall a. DropdownItem a -> Widget HTML a
renderDropdownItem { label, value } = D.a
    [ value <$ P.onClick, P.className "whitespace-nowrap" ]
    [ D.text label ]

-- | Renders the given button widget, shows the dropdown menu when it returns,
-- | and then returns whatever was clicked in the menu.
dropdownButton :: forall a b. Widget HTML b -> Array (DropdownItem a) -> Widget HTML a
dropdownButton btn items = dropdownButton_ btn $ map renderDropdownItem items

-- Like `dropdownButton` but accepts arbitrary widgets as its contents.
dropdownButton_ :: forall a b. Widget HTML b -> Array (Widget HTML a) -> Widget HTML a
dropdownButton_ btn widgets = do
    -- Wait for a button click.
    _ <- dropdownContainer [ btn ]
    -- When button is clicked, render the dropdown
    (dropdownContainer [ loopInfinitely btn, dropdownMenu_ widgets ]) >>= case _ of
        -- If an item was clicked, return it.
        Just res -> pure res
        -- If the menu was closed, we got `Nothing`, so render the button again.
        Nothing -> dropdownButton_ btn widgets


-- | Renders a dropdown menu with the given array of items.
dropdownMenu :: forall a. Array (DropdownItem a) -> Widget HTML (Maybe a)
dropdownMenu items = dropdownMenu_ $ map renderDropdownItem items

-- Like `dropdownMenu` but accepts arbitrary widgets as its contents.
dropdownMenu_ :: forall a. Array (Widget HTML a) -> Widget HTML (Maybe a)
dropdownMenu_ widgets = dropdownDiv
    [ P.className "dropdown-menu"
    , Nothing <$ P.onBlur
    ] $ map (Just <$> _) widgets

dropdownContainer :: forall a. Array (Widget HTML a) -> Widget HTML a
dropdownContainer = D.div [ P.className "dropdown-container" ]

foreign import classDropdownDiv :: forall a. ReactClass a

dropdownDiv :: forall a b.
    ShiftMap (Widget (Array ReactElement)) b
    => MultiAlternative b
    => Array (ReactProps a)
    -> Array (b a)
    -> b a
dropdownDiv props =
    (D.el' (unsafeCreateElement classDropdownDiv <<< RDP.unsafeFromPropsArray)) props

